import React, { useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { addDays, parseISO, format } from 'date-fns';
import { ArrowLeft, Check, CreditCard, Download, Home, MapPin, Package, RotateCcw, ShieldCheck, Truck } from 'lucide-react';
import { Button } from '../components/Button';
import { OrderStatusBadge } from '../components/OrderStatusBadge';
import { PillRow } from '../components/PillRow';
import { usePlatform } from '../contexts/PlatformContext';
import { labReport } from '../data/biomarkers';
import { pills } from '../data/pills';
import { pregnancyPlans } from '../data/pregnancyPlans';
import { formatAED } from '../utils/pack';
import { formatOrderDate } from '../utils/orders';
import type { Pill } from '../types/platform';
export function OrderDetail() {
  const {
    orderId
  } = useParams();
  const {
    orders,
    goal,
    selectPlan,
    setPackPills
  } = usePlatform();
  const navigate = useNavigate();
  const [invoiceReady, setInvoiceReady] = useState(false);
  const order = orders.find(o => o.id === orderId);
  if (!order) {
    return <div className="rounded-2xl border border-dashed border-white/10 p-12 text-center" data-id="element-235" data-mp="16l5u8kzvusq7">
        <p className="font-medium" data-id="element-236" data-mp="1kcaxry6dg55t">Order not found</p>
        <p className="mt-1 text-sm text-muted-foreground" data-id="element-237" data-mp="1kazmzeeidx57">It may belong to a different profile.</p>
        <Button variant="outline" className="mt-6" onClick={() => navigate('/orders')} data-id="element-238" data-mp="1feiyuk7b7is4">Back to orders</Button>
      </div>;
  }
  const items = order.pillIds.map(id => pills.find(p => p.id === id)).filter((p): p is Pill => Boolean(p));
  const placed = parseISO(order.placedAt);
  const d = (n: number) => format(addDays(placed, n), 'd MMM');
  const vat = order.total - order.total / 1.05;
  const stepIndex = {
    Processing: 1,
    'In transit': 3,
    Delivered: 4,
    Cancelled: 0
  }[order.status];
  const steps = [{
    icon: Check,
    label: 'Placed',
    date: d(0)
  }, {
    icon: ShieldCheck,
    label: 'Doctor verified',
    date: d(1)
  }, {
    icon: Package,
    label: 'Packed',
    date: d(2)
  }, {
    icon: Truck,
    label: 'Shipped',
    date: d(2)
  }, {
    icon: Home,
    label: 'Delivered',
    date: order.deliveredAt ? formatOrderDate(order.deliveredAt, 'd MMM') : d(4)
  }];
  const reorder = () => {
    if (goal === 'pregnancy') {
      const plan = pregnancyPlans.find(p => `${p.name} pack` === order.label);
      if (plan) selectPlan(plan.id);
    } else {
      setPackPills(order.pillIds);
    }
    navigate('/plan');
  };
  return <div data-id="element-239" data-mp="1wcisqaosrrqt">
      <Link to="/orders" className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground" data-id="element-240" data-mp="1wcr6e4sqv132">
        <ArrowLeft className="size-4" data-id="element-241" data-mp="26c9mga5ja2pu" /> All orders
      </Link>

      <div className="mt-6 flex flex-col justify-between gap-4 md:flex-row md:items-end" data-id="element-242" data-mp="26aorwkj7yjrz">
        <div data-id="element-243" data-mp="2b4t2ddrp19xt">
          <p className="font-mono text-xs uppercase tracking-widest text-[#9b8cf0]" data-id="element-244" data-mp="2b5b60vgoqqfr">{order.id}</p>
          <h1 className="mt-2 flex flex-wrap items-center gap-3 text-3xl font-medium tracking-tight" data-id="element-245" data-mp="1dm9okv9g9pwh">
            {order.label}
            <OrderStatusBadge status={order.status} data-id="element-246" data-mp="1b47mz1336qe3" />
          </h1>
          <p className="mt-2 text-muted-foreground" data-id="element-247" data-mp="1b1brdjepodst">Placed {formatOrderDate(order.placedAt)} · 30 daily sachets</p>
        </div>
        <div className="flex gap-2" data-id="element-248" data-mp="114yxakf97xxj">
          <Button variant="outline" onClick={() => {
          setInvoiceReady(true);
          setTimeout(() => setInvoiceReady(false), 2000);
        }} data-id="element-249" data-mp="113x9dfd53gye">
            {invoiceReady ? <Check className="size-4" data-id="element-250" data-mp="s6wp9kx3s50r" /> : <Download className="size-4" data-id="element-251" data-mp="nedd8se2dhy2" />}
            {invoiceReady ? 'Invoice downloaded' : 'Invoice'}
          </Button>
          <Button className="bg-[#9b8cf0] text-[#0f1017] hover:bg-[#b0a4f5]" onClick={reorder} data-id="element-252" data-mp="ie7deoxn4p4w">
            <RotateCcw className="size-4" data-id="element-253" data-mp="dgp776b0co0l" /> Reorder
          </Button>
        </div>
      </div>

      {order.status === 'Cancelled' ? <div className="mt-8 rounded-2xl border border-white/10 bg-white/[0.02] p-5 text-sm text-muted-foreground" data-id="element-254" data-mp="8g1e8f37xx3q">
          This order was cancelled before packing. No payment was taken.
        </div> : <ol className="mt-8 grid grid-cols-5 gap-2 rounded-2xl border border-white/10 bg-white/[0.02] p-5" data-id="element-255" data-mp="t880pbp337ar">
          {steps.map((s, i) => {
        const done = i < stepIndex;
        const active = i === stepIndex;
        return <li key={s.label} className="flex flex-col items-center text-center" data-id="element-256" data-mp="oe3tcrbwox95" data-mpi={i}>
                <span className={`flex size-9 items-center justify-center rounded-full ${done ? 'bg-[#9b8cf0] text-[#0f1017]' : active ? 'border border-[#9b8cf0] text-[#c4baff]' : 'border border-white/15 text-muted-foreground'}`} data-id="element-257" data-mp="aeulqg1rnbgs">
                  <s.icon className="size-4" data-id="element-258" data-mp="5fj6xckymg1u" />
                </span>
                <p className={`mt-2 text-xs sm:text-sm ${done || active ? '' : 'text-muted-foreground'}`} data-id="element-259" data-mp="1vsqohmxkomi0">{s.label}</p>
                <p className="hidden font-mono text-[11px] text-muted-foreground sm:block" data-id="element-260" data-mp="1zuu3gljga981">{s.date}</p>
              </li>;
      })}
        </ol>}

      <div className="mt-8 grid gap-8 lg:grid-cols-[1fr_320px]" data-id="element-261" data-mp="3ka0a8thbtx7z">
        <section aria-labelledby="contents-heading" data-id="element-262" data-mp="3gccclio91qce">
          <h2 id="contents-heading" className="text-lg font-medium" data-id="element-263" data-mp="3u1wbgdepm3gf">What was in this pack</h2>
          <ul className="mt-4 space-y-2" data-id="element-264" data-mp="3pbpo0gvxoozr">
            {items.map(p => <PillRow key={p.id} pill={p} data-id="element-265" data-mp="3p9czfkvo7gdq" />)}
          </ul>
          <p className="mt-4 flex items-center gap-2 text-sm text-muted-foreground" data-id="element-266" data-mp="7teixgyh68sj">
            <ShieldCheck className="size-4 text-[#9b8cf0]" data-id="element-267" data-mp="2utltqeis58b" /> Verified by {labReport.reviewer}
          </p>
        </section>

        <aside className="space-y-4" data-id="element-268" data-mp="guuu3qx46ext">
          <section className="rounded-2xl border border-white/10 bg-white/[0.02] p-5" data-id="element-269" data-mp="bwt1ofdst39o">
            <h2 className="font-medium" data-id="element-270" data-mp="2in0ycvvpoa1z">Payment</h2>
            <dl className="mt-4 space-y-2 text-sm" data-id="element-271" data-mp="2insi99fy4jcf">
              <div className="flex justify-between" data-id="element-272" data-mp="2do63gvkgyv1i"><dt className="text-muted-foreground" data-id="element-273" data-mp="2dozbs0z2tslr">Subtotal</dt><dd data-id="element-274" data-mp="hkrm1scbe7ls">{formatAED(Math.round(order.total - vat))}</dd></div>
              <div className="flex justify-between" data-id="element-275" data-mp="hmm9bdb26bt1"><dt className="text-muted-foreground" data-id="element-276" data-mp="mjmezn3zxcjm">VAT (5%)</dt><dd data-id="element-277" data-mp="mkebd05f3eay">{formatAED(Math.round(vat))}</dd></div>
              <div className="flex justify-between" data-id="element-278" data-mp="rj7h26egq10k"><dt className="text-muted-foreground" data-id="element-279" data-mp="ri64fxhst6gq">Delivery</dt><dd data-id="element-280" data-mp="vkki836m1g15">Free</dd></div>
              <div className="flex justify-between border-t border-white/10 pt-2 font-medium" data-id="element-281" data-mp="vllwegixain3">
                <dt data-id="element-282" data-mp="10kf8q5mh43lo">{order.status === 'Cancelled' ? 'Not charged' : 'Total paid'}</dt>
                <dd data-id="element-283" data-mp="15i70mzn4bppv">{formatAED(order.total)}</dd>
              </div>
            </dl>
            <p className="mt-4 flex items-center gap-2 text-sm text-muted-foreground" data-id="element-284" data-mp="1a9p3xucm62e0">
              <CreditCard className="size-4" data-id="element-285" data-mp="1f8jx9q87j9cd" /> {order.payment}
            </p>
          </section>
          <section className="rounded-2xl border border-white/10 bg-white/[0.02] p-5" data-id="element-286" data-mp="1facwikghqrac">
            <h2 className="font-medium" data-id="element-287" data-mp="1j8281t6be41h">Delivery</h2>
            <p className="mt-3 flex gap-2 text-sm" data-id="element-288" data-mp="1oa9nisqcs28k">
              <MapPin className="mt-0.5 size-4 shrink-0 text-muted-foreground" data-id="element-289" data-mp="1o6vj7ey4piyh" /> {order.address}
            </p>
          </section>
        </aside>
      </div>
    </div>;
}